/**
 * Responsibility: Implement world definition model behavior for the ui/wizard surface.
 * Scope: Owns ui/wizard concerns only; cross-surface orchestration is excluded.
 */
export type WorldDefinitionDraft = {
  inspiration?: string
  modifiers?: string
  narrativeType?: string
}

function clean(value: unknown): string {
  return String(value ?? '').trim()
}

export class WorldDefinitionModel {
  inspiration: string
  modifiers: string
  narrativeType: string

  constructor(draft: WorldDefinitionDraft = {}) {
    this.inspiration = clean(draft.inspiration)
    this.modifiers = clean(draft.modifiers)
    this.narrativeType = clean(draft.narrativeType)
  }

  static fromValues(values: unknown): WorldDefinitionModel {
    if (!values || typeof values !== 'object') return new WorldDefinitionModel()
    return new WorldDefinitionModel(values as WorldDefinitionDraft)
  }

  isEmpty(): boolean {
    return !this.inspiration && !this.modifiers && !this.narrativeType
  }

  isValid(): boolean {
    return !!this.inspiration
  }

  missingField(): 'inspiration' | null {
    return this.inspiration ? null : 'inspiration'
  }

  toDraft(): WorldDefinitionDraft {
    return {
      inspiration: this.inspiration,
      modifiers: this.modifiers,
      narrativeType: this.narrativeType,
    }
  }

  toSeed(): string {
    const parts = [
      this.inspiration && `Inspiration: ${this.inspiration}`,
      this.modifiers && `Modifiers: ${this.modifiers}`,
      this.narrativeType && `Narrative type: ${this.narrativeType}`,
    ].filter(Boolean)
    return parts.join('\n')
  }

  summary(): string {
    if (this.inspiration) return this.inspiration
    return 'World inspiration not defined yet.'
  }
}
